import { Type } from '@angular/core';
import { CsPageClass, CsPageComponentInterface } from 'providers/nav-controller';
import { isPageRef, PageRef, CallablePageRef } from 'providers/page-ref';
import { parseUrlToPages } from 'utils/url-parser';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';

let allDeepLinks: DeepLinkMetadata[] = [];

/**
 * Every `defaultHistory` array `deriveDefaultHistories()` has produced.
 *
 * It exists so that running the derivation twice over one list is safe: on the
 * second run each row already carries a derived array, and without this the
 * "you declared both a parent and a hand-written trail" check would fire on a
 * page list that is perfectly correct. A WeakSet, so a discarded list is
 * collected normally.
 */
const derivedHistories = new WeakSet<Type<CsPageComponentInterface<any>>[]>();

export function setAllDeepLinks(deepLinks: DeepLinkMetadata[]) {
  // validate all page selectors start with 'page-'
  const invalidPages: string[] = [];
  for (const link of deepLinks) {
    if (link.component) {
      const annotations = (link.component as any).__annotations__;
      const selector = annotations?.[0]?.selector;
      if (selector && !selector.startsWith('page-')) {
        invalidPages.push(`  - ${link.name}: '${selector}'`);
      }
    }
  }
  if (invalidPages.length > 0) {
    throw new Error(
      `Page components with invalid selectors (must start with 'page-' for cs-content header detection):\n` +
      invalidPages.join('\n')
    );
  }
  deriveDefaultHistories(deepLinks);
  allDeepLinks = deepLinks;
}

/**
 * Fills in `defaultHistory` for every row that declares a `parent`.
 *
 * A row names ONE parent — the page Back should land on — and this walks the
 * parent chain up to the root to produce the array the rest of the app reads.
 * The array is bottom-of-stack first, so a child's array is its parent's array
 * with the parent appended. See design-docs/navigation.md.
 *
 * Writing the derived array back onto the row is what makes the change
 * invisible to every reader: `getPageDefaultHistory`, `NavController`, and
 * `UrlService.createSegment` (which spreads the row) all keep reading
 * `defaultHistory` exactly as before.
 *
 * Everything that can go wrong here throws, because a broken chain otherwise
 * shows up much later as a Back button that goes somewhere plausible but wrong.
 *
 * Running it twice over the same list is safe and produces the same answer: an
 * array this function wrote is not mistaken for one an author hand-wrote.
 */
export function deriveDefaultHistories(deepLinks: DeepLinkMetadata[]): void {
  // for each row, find the row that registers its parent component
  const rowByComponent = new Map<DeepLinkMetadata['component'], DeepLinkMetadata>();
  for (const link of deepLinks) {
    // a component registered twice (IntakeFormPage) keeps its first row, which is
    // the one every other lookup in this file finds too
    if (link.component && !rowByComponent.has(link.component)) {
      rowByComponent.set(link.component, link);
    }
  }

  for (const link of deepLinks) {
    if (!link.parent) continue;
    // if the row still carries a trail somebody typed, refuse — but an array
    // this function wrote on an earlier run is not that, so re-registering the
    // same list stays safe rather than blaming a page list that is correct
    if (link.defaultHistory && link.defaultHistory.length > 0 && !derivedHistories.has(link.defaultHistory)) {
      throw new Error(
        `Page '${link.name}' declares both a parent and a hand-written defaultHistory. ` +
        `Declare only the parent — the trail is derived from it.`
      );
    }

    // walk parents to the root, collecting them nearest-first
    const chainNearestFirst: Type<CsPageComponentInterface<any>>[] = [];
    const seen = new Set<DeepLinkMetadata['component']>([link.component]);
    let ancestor = link.parent;
    while (ancestor) {
      if (seen.has(ancestor)) {
        throw new Error(
          `Page '${link.name}' has a parent chain that loops back on itself. ` +
          `Every chain must end at a page with no parent.`
        );
      }
      seen.add(ancestor);
      const ancestorRow = rowByComponent.get(ancestor);
      if (!ancestorRow) {
        throw new Error(
          `Page '${link.name}' names a parent that is not registered in the page list. ` +
          `A parent must be a page with its own row.`
        );
      }
      chainNearestFirst.push(ancestor);
      ancestor = ancestorRow.parent;
    }

    // the array reads bottom-of-stack first, so the root comes first and the
    // immediate parent comes last
    const derived = chainNearestFirst.reverse();
    derivedHistories.add(derived);
    link.defaultHistory = derived;
  }
}

export function getAllDeepLinks(): DeepLinkMetadata[] {
  return allDeepLinks;
}

export function getPageHead(pageName: string): string {
  let page = allDeepLinks.find(page => page.name === pageName);
  if (!page) {
    return undefined;
  }
  let name = page.segment.match(/^[^\/]+/)[0]; // all until eventual slash
  console.assert(name && name.length > 0);
  return name;
}
/** Returns the full path from page name.
 E.g. for `HomePage` returns `/home/:day` */
export function getPageFullPath(pageName: string): string {
  let page = allDeepLinks.find(page => page.name === pageName);
  return page && '/' + page.segment;
}
/**
 * Returns the page class from page name, PageRef, or CallablePageRef.
 * E.g. for `HomePage` or `IHomePage` returns the object representing the HomePage component.
 */
export function getPageClass(nameOrRef: string | PageRef<any, any> | CallablePageRef<any, any>): CsPageClass<any> {
  const pageName = isPageRef(nameOrRef) ? nameOrRef.pageName : nameOrRef;
  let page = allDeepLinks.find(page => page.name === pageName);
  return page && page.component as CsPageClass<any>;
}

/**
 * Returns a PageRef for dynamic navigation from a page name string.
 * Use this when the target page is determined at runtime.
 * E.g. `getPageInterface('HomePage')` returns a PageRef for HomePage.
 */
export function getPageInterface(pageName: string): PageRef<any, any> {
  return { pageName } as PageRef<any, any>;
}

/**
 * Return the page name from class or PageRef.
 */
export function getPageName(pageClassOrRef: any): string {
  // if it's a PageRef, return the pageName directly
  if (isPageRef(pageClassOrRef)) {
    return pageClassOrRef.pageName;
  }
  let page = allDeepLinks.find(page => page.component === pageClassOrRef);
  return page && page.name;
}

/** Return the page segment from class (for use in breadcrumbs). */
export function getPageSegment(pageClass: any): string {
  let page = allDeepLinks.find(page => page.component === pageClass);
  if (!page || !page.segment) {
    return undefined;
  }
  // Return base segment name (before any parameters like :id)
  return page.segment.split('/')[0];
}

/** Return the page class from segment (for decoding breadcrumbs). */
export function getPageClassBySegment(segment: string): Type<CsPageComponentInterface<any>> {
  let page = allDeepLinks.find(page => {
    if (!page.segment) return false;
    // Match against base segment name (before any parameters)
    return page.segment.split('/')[0] === segment;
  });
  return page && page.component;
}
/** Returns the defaultHistory array from page name. */
export function getPageDefaultHistory(pageName: string): Type<CsPageComponentInterface<any>>[] {
  let page = allDeepLinks.find(page => page.name === pageName);
  return page && page.defaultHistory;
}
export function getPageNameByPath(path: string): string {
  // Use the unified parser
  const pages = parseUrlToPages(path);

  if (pages.length === 0) {
    return undefined;
  }

  // Get the current page (last in array)
  const currentPage = pages[pages.length - 1];

  // Find matching page in deep links by segment name
  const matchingPage = allDeepLinks.find(link => {
    if (!link.segment) return false;
    const linkSegmentName = link.segment.split('/')[0];
    return linkSegmentName === currentPage.segment;
  });

  return matchingPage ? matchingPage.name : undefined;
}


/**
 * Translated browser-tab titles, keyed by the page name in `app-page-list.ts`.
 *
 * The `title` field in that list stays the English literal on purpose — it is
 * the stable identifier, and it is the fallback for every page missing from
 * this map. Admin, testing and developer-only pages are deliberately absent:
 * they are internal tools and keep their English titles.
 *
 * Built LAZILY, on first call, and then cached. The laziness is about IMPORT
 * ORDER, not about language: `LL` is a synchronously-initialized module
 * constant and switching language reloads the whole app, so the language can
 * never be "wrong" once the app is running. What we are avoiding is an import
 * cycle — if this module were evaluated before `i18n.service`, reading `LL` at
 * module scope would see an undefined binding. Do not "optimize" this back to a
 * module-level constant.
 *
 * `client/src/app/app-page-titles.test.ts` asserts this map stays in step with
 * `app-page-list.ts`, so a page added or renamed there fails a test instead of
 * silently shipping an English tab title.
 */
let translatedPageTitlesCache: Record<string, () => TranslatedString> | undefined;

export function getTranslatedPageTitles(): Record<string, () => TranslatedString> {
  if (translatedPageTitlesCache) {
    return translatedPageTitlesCache;
  }
  translatedPageTitlesCache = {
    LoginPage: LL.pageTitles.login,
    FatalErrorPage: LL.pageTitles.fatalError,
    SubscribePage: LL.pageTitles.subscribe,
    UnsubscribePage: LL.pageTitles.unsubscribe,
    ChangelogPage: LL.pageTitles.changelog,
    HomePage: LL.pageTitles.home,
    EditMealPage: LL.pageTitles.editMeal,
    WorkoutDetailPage: LL.pageTitles.workoutDetail,
    EquipmentEditPage: LL.pageTitles.equipmentEdit,
    EditWeighInPage: LL.pageTitles.editWeighIn,
    TrainingPage: LL.pageTitles.training,
    EditWorkoutsPage: LL.pageTitles.editWorkouts,
    NutritionPage: LL.pageTitles.nutrition,
    ProgramSettingsPage: LL.pageTitles.programSettings,
    AdvancedTrainingMethodsPage: LL.pageTitles.advancedTrainingMethods,
    NutritionOptionsPage: LL.pageTitles.nutritionOptions,
    ScheduleTipsPage: LL.pageTitles.scheduleTips,
    EquipmentSearchPage: LL.pageTitles.equipmentSearch,
    FoodsPage: LL.pageTitles.foods,
    BarcodeScannerPage: LL.pageTitles.barcodeScanner,
    LabelScannerPage: LL.pageTitles.labelScanner,
    MealPhotoScannerPage: LL.pageTitles.mealPhotoScanner,
    EquipmentScannerPage: LL.pageTitles.equipmentScanner,
    WorkoutPlanScannerPage: LL.pageTitles.workoutPlanScanner,
    RecipeScannerPage: LL.pageTitles.recipeScanner,
    MealPlanPage: LL.pageTitles.mealPlan,
    EditFoodPage: LL.pageTitles.editFood,
    EditNutritionPlanPage: LL.pageTitles.editNutritionPlan,
    EditIngredientPage: LL.pageTitles.editIngredient,
    ExerciseSwitchPage: LL.pageTitles.exerciseSwitch,
    EditExercisePage: LL.pageTitles.editExercise,
    ViewExercisePage: LL.pageTitles.viewExercise,
    ExerciseConfigurationPage: LL.pageTitles.exerciseConfiguration,
    ExerciseDiffPage: LL.pageTitles.exerciseDiff,
    MuscleVolumePage: LL.pageTitles.muscleVolume,
    EditTrainingGoalPage: LL.pageTitles.editTrainingGoal,
    UpdatePlanOptionsPage: LL.pageTitles.updatePlanOptions,
    WeeklyCheckupPage: LL.pageTitles.weeklyCheckup,
    ExerciseStatsPage: LL.pageTitles.exerciseStats,
    ProgressPage: LL.pageTitles.progress,
    UserProfilePage: LL.pageTitles.userProfile,
    SettingsPage: LL.pageTitles.settings,
    AppSettingsPage: LL.pageTitles.appSettings,
    NotificationSettingsPage: LL.pageTitles.notificationSettings,
    CustomizeHomepagePage: LL.pageTitles.customizeHomepage,
    RedeemCodePage: LL.pageTitles.redeemCode,
    ReferralInvitePage: LL.pageTitles.referralInvite,
    PromoPage: LL.pageTitles.promo,
    ReferralHubPage: LL.pageTitles.referralHub,
    IntakeFormPage: LL.pageTitles.intakeForm,
    IntroCarouselPage: LL.pageTitles.introCarousel,
    AwaitingCoachPage: LL.pageTitles.awaitingCoach,
    HelpCardPage: LL.pageTitles.helpCard,
    MealSuggestionsPage: LL.pageTitles.mealSuggestions,
    NotePage: LL.pageTitles.note,
    PurchaseSuccessPage: LL.pageTitles.purchaseSuccess,
    NutritionInfoPage: LL.pageTitles.nutritionInfo,
    TaskCalendarPage: LL.pageTitles.taskCalendar,
    InputCalendarPage: LL.pageTitles.inputCalendar,
    ChangeWorkoutPage: LL.pageTitles.changeWorkout,
    TrainingOverviewPage: LL.pageTitles.trainingOverview,
    SendFeedbackPage: LL.pageTitles.sendFeedback,
    SendCoachMessagePage: LL.pageTitles.sendCoachMessage,
    HelpPage: LL.pageTitles.help,
    CliAuthPage: LL.pageTitles.cliAuth,
    CoachSettingsPage: LL.pageTitles.coachSettings,
    CoachClientDetailPage: LL.pageTitles.coachClientDetail,
    CoachBillingPage: LL.pageTitles.coachBilling,
    CoachCheckoutPage: LL.pageTitles.coachCheckout,
    CoachBrandingPage: LL.pageTitles.coachBranding,
    CoachClaimPage: LL.pageTitles.coachClaim,
  };
  return translatedPageTitlesCache;
}

/**
 * Page names that intentionally have NO entry in the translated map and keep
 * their English title from `app-page-list.ts`. Admin, testing and developer-only
 * pages are internal tools — see the scope boundary in #4317.
 *
 * Exported so the tripwire test can tell "deliberately English" apart from
 * "someone forgot to add a key".
 */
export const UNTRANSLATED_PAGE_NAME_PREFIXES = ['Admin', 'Test', 'Db'];

export function getPageTitleByName(pageName: string): TranslatedString | undefined {
  let page = allDeepLinks.find(page => page.name === pageName);
  if (!page) return undefined;
  // translate when we have a key for this page (#4317); admin / testing pages
  // have none and fall back to the English literal from the page list -- that
  // fallback is deliberately untranslated (#4324 Phase 2), see the class
  // comment on UNTRANSLATED_PAGE_NAME_PREFIXES above
  const translated = getTranslatedPageTitles()[page.name];
  return translated ? translated() : untranslatedString(page.title);
}

export function getAllowedPageDuringWelcome(): string[] {
  return allDeepLinks.filter(page => page.allowAtWelcome).map(page => page.name);
}

/**
 * The screens that show a coach their OWN coaching account. They must not open
 * while the coach is inside a client's account, because the client's database is
 * the open one and every coach-side read then comes back empty without failing.
 * `AppComponent.pageFilterAsync` is what enforces that, and it reaches every
 * route because the filter runs at page CONSTRUCTION in `CsNavComponent`, not
 * per navigation method.
 *
 * Which pages are on the list, which two are deliberately left off, why a hidden
 * menu row was not enough, and why the navigation call sites look like they
 * bypass the filter when they do not: `design-docs/coach-mode.md`, "Reading the
 * coach's own roster".
 */
export const COACH_ONLY_PAGE_NAMES = [
  'CoachSettingsPage',
  'CoachClientDetailPage',
  'CoachBillingPage',
  'CoachBrandingPage',
];

/** Root page names that show bottom nav on small screens */
export const ROOT_PAGE_NAMES = ['HomePage', 'TrainingPage', 'NutritionPage', 'SettingsPage'];

/** Root page selectors (lowercase tag names) that show bottom nav on small screens */
export const ROOT_PAGE_SELECTORS = ['page-home', 'page-training', 'page-nutrition', 'page-settings'];

/**
 * Check if a page name is a root page (shows bottom nav on small screens).
 * @param pageName Page class name like 'HomePage', 'SettingsPage'
 */
export function isRootPageName(pageName: string): boolean {
  return ROOT_PAGE_NAMES.includes(pageName);
}

/**
 * Check if a page element selector is a root page (shows bottom nav on small screens).
 * @param selector Page element selector like 'page-home', 'page-settings'
 */
export function isRootPageSelector(selector: string): boolean {
  return ROOT_PAGE_SELECTORS.includes(selector?.toLowerCase());
}

export interface DeepLinkMetadata {
  component?: Type<CsPageComponentInterface<any>>;
  loadChildren?: string;
  name?: string;
  title: string;
  segment?: string;
  /**
   * The page Back should land on when this one is opened with no real
   * navigation history. `setAllDeepLinks()` walks the chain of parents and
   * fills in `defaultHistory` from it, so a row states one parent instead of
   * copying out the whole ancestor chain.
   *
   * This is the back target, NOT the position in the side menu. A page that
   * sits under one menu but comes back to a different screen simply names that
   * screen — `CoachCheckoutPage` sits under Coaching and names User Profile.
   */
  parent?: Type<CsPageComponentInterface<any>>;
  /**
   * Derived from `parent` at registration time — do NOT write one by hand.
   * A row that deliberately has NO fallback trail says `defaultHistory: []`.
   */
  defaultHistory?: Type<CsPageComponentInterface<any>>[];
  priority?: string;
  allowAtWelcome?: boolean;
}
